// src/features/attachment/LookupByIdsDialog.tsx
// "Open by ID" — resolve a pasted list of attachment IDs (e.g. copied from
// another system, a bulk-export list, or a support ticket) via
// ListAttachmentsByIDs. Authorization is checked per-attachment server
// side, so only attachments the caller can see come back.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { attachmentApi } from '@core/api';
import type { Attachment } from '@core/types/attachment';
import { errMessage } from './format';

export interface LookupByIdsDialogProps {
  open: boolean;
  onClose: () => void;
  onFound: (attachments: Attachment[]) => void;
}

export function LookupByIdsDialog({ open, onClose, onFound }: LookupByIdsDialogProps) {
  const { t } = useI18n();
  const [raw, setRaw] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleSubmit = async () => {
    const ids = raw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await attachmentApi.ListAttachmentsByIDs({ ids, include_download_urls: true });
      const found = res.attachments?.attachments ?? [];
      onFound(found);
      setRaw('');
      onClose();
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.lookup.error')));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" className="relative mx-4 w-full max-w-md rounded-lg border border-border bg-background p-5 shadow-xl">
        <h3 className="text-base font-semibold text-foreground">{t('attachment.manager.lookup.title')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('attachment.manager.lookup.description')}</p>
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={4}
          placeholder={t('attachment.manager.lookup.placeholder')}
          className="mt-3 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted">
            {t('common.cancel')}
          </button>
          <Button size="sm" onClick={handleSubmit} isLoading={isLoading} disabled={!raw.trim()}>
            {t('attachment.manager.lookup.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
